import { useState, type FormEvent } from "react";
import { Handshake, History, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { BORDER, FormDrawer } from "@/components/shared/FormDrawer";
import { ExportButton } from "@/components/ui/ExportButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserOptions } from "@/modules/users/hooks/useUsers";
import {
  useCompanyOwnership,
  usePartnerList,
  useRemoveOwnership,
  useSaveOwnership,
  useSavePartner,
  type CompanyOwnership,
  type Partner,
} from "../hooks/usePartners";

function PartnerDrawer({ partner, onClose }: { partner: Partner | null; onClose: () => void }) {
  const save = useSavePartner();
  const { data: users = [] } = useUserOptions();
  const [name, setName] = useState(partner?.name ?? "");
  const [shortCode, setShortCode] = useState(partner?.shortCode ?? "");
  const [userId, setUserId] = useState(partner?.userId ?? "");
  const [isActive, setIsActive] = useState(partner?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({
        id: partner?.id,
        payload: {
          name,
          shortCode,
          userId: userId || null,
          ...(partner ? { isActive } : {}),
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer
      title={partner ? "Edit Partner" : "New Partner"}
      onClose={onClose}
      onSubmit={handleSubmit}
      error={error}
      busy={save.isPending}
    >
      <div className="space-y-2">
        <Label htmlFor="ptr-name">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input id="ptr-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ptr-short">
          Short code <span className="text-destructive">*</span>
        </Label>
        <Input
          id="ptr-short"
          value={shortCode}
          onChange={(e) => setShortCode(e.target.value)}
          maxLength={10}
          placeholder="e.g. AJ"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ptr-user">Linked user</Label>
        <Select id="ptr-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">No login</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
      </div>
      {partner && (
        <div className="space-y-2">
          <Label htmlFor="ptr-status">Status</Label>
          <Select
            id="ptr-status"
            value={isActive ? "active" : "inactive"}
            onChange={(e) => setIsActive(e.target.value === "active")}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
      )}
    </FormDrawer>
  );
}

function OwnershipDrawer({
  company,
  partners,
  onClose,
}: {
  company: CompanyOwnership;
  partners: Partner[];
  onClose: () => void;
}) {
  const save = useSaveOwnership();
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [shares, setShares] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (company.current?.holders ?? []).map((h) => [h.partnerId, String(h.shares)]),
    ),
  );
  const [error, setError] = useState<string | null>(null);

  const entered = partners
    .map((p) => ({ partner: p, shares: Number(shares[p.id] || 0) }))
    .filter((e) => e.shares > 0);
  const total = entered.reduce((sum, e) => sum + e.shares, 0);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (entered.length === 0) {
      setError(
        partners.length === 0
          ? "Add a partner before setting ownership"
          : "Enter shares for at least one partner",
      );
      return;
    }
    setError(null);
    try {
      await save.mutateAsync({
        companyId: company.id,
        effectiveFrom,
        holders: entered.map((h) => ({ partnerId: h.partner.id, shares: h.shares })),
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer
      title={`Set Ownership: ${company.name}`}
      onClose={onClose}
      onSubmit={handleSubmit}
      error={error}
      busy={save.isPending}
    >
      <p className="text-xs text-muted-foreground">
        Earlier ownership is kept as history. Saving on a date that already has a record replaces
        it. Shares are relative: 1, 1 and 1 means exactly one third each.
      </p>
      <div className="space-y-2">
        <Label htmlFor="own-date">
          Effective from <span className="text-destructive">*</span>
        </Label>
        <Input
          id="own-date"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        {partners.length === 0 && (
          <div className="rounded-md border bg-[hsl(210,50%,98%)] px-3 py-2 text-sm text-muted-foreground" style={BORDER}>
            There are no active partners yet. Close this and use <strong>New Partner</strong> to add
            them, then set ownership.
          </div>
        )}
        {partners.map((p) => {
          const value = Number(shares[p.id] || 0);
          return (
            <div key={p.id} className="grid grid-cols-[1fr_100px_70px] items-center gap-3">
              <Label htmlFor={`own-${p.id}`} className="font-normal">
                {p.name} ({p.shortCode})
              </Label>
              <Input
                id={`own-${p.id}`}
                type="number"
                min="0"
                step="any"
                value={shares[p.id] ?? ""}
                onChange={(e) => setShares({ ...shares, [p.id]: e.target.value })}
                placeholder="0"
              />
              <span className="text-right text-sm text-muted-foreground">
                {value > 0 ? `${((value / total) * 100).toFixed(2)}%` : "—"}
              </span>
            </div>
          );
        })}
      </div>
    </FormDrawer>
  );
}

function OwnershipCard({
  company,
  canEdit,
  canDelete,
  onEdit,
  onRemove,
  onActivity,
}: {
  company: CompanyOwnership;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onRemove: (effectiveFrom: string) => void;
  onActivity: () => void;
}) {
  const earlierOrFuture = company.history.filter(
    (s) => s.effectiveFrom !== company.current?.effectiveFrom,
  );
  return (
    <div className="rounded-xl border bg-white p-5" style={BORDER}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-foreground">{company.name}</h3>
          <p className="text-xs text-muted-foreground">
            {company.code} · {company.currency} ·{" "}
            {company.current
              ? `In force since ${formatDate(company.current.effectiveFrom)}`
              : "No ownership in force"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onActivity}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="Activity history"
          >
            <History className="h-[14px] w-[14px]" strokeWidth={1.5} />
          </button>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              Set ownership
            </Button>
          )}
        </div>
      </div>
      {company.current && (
        <table className="mt-4 w-full text-sm">
          <tbody>
            {company.current.holders.map((h) => (
              <tr key={h.partnerId} className="border-t" style={BORDER}>
                <td className="py-2">{h.partnerName}</td>
                <td className="py-2 text-right text-muted-foreground">{h.percent.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {company.current && (
        <p className="mt-3 text-xs text-muted-foreground">
          Transfers sent from this company split by this ownership by default, and can be edited
          per transfer.
        </p>
      )}
      {(earlierOrFuture.length > 0 || (canDelete && company.current)) && (
        <div className="mt-4 space-y-1 border-t pt-3" style={BORDER}>
          <div className="text-xs font-medium text-muted-foreground">History</div>
          {company.history.map((s) => (
            <div key={s.effectiveFrom} className="flex items-center justify-between text-xs">
              <span>
                {formatDate(s.effectiveFrom)}:{" "}
                {s.holders.map((h) => `${h.shortCode} ${h.percent.toFixed(2)}%`).join(", ")}
              </span>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => onRemove(s.effectiveFrom)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-red-50 hover:text-red-600"
                  title="Remove this record"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PartnersPage() {
  const { user } = useAuth();
  const permissions = user?.permissions?.partners;
  const { data: partners = [], isLoading, isError, error } = usePartnerList();
  const { data: companies = [] } = useCompanyOwnership();
  const removeOwnership = useRemoveOwnership();

  const [partnerDrawer, setPartnerDrawer] = useState<{ partner: Partner | null } | null>(null);
  const [ownershipCompany, setOwnershipCompany] = useState<CompanyOwnership | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{
    company: CompanyOwnership;
    effectiveFrom: string;
  } | null>(null);
  const [activity, setActivity] = useState<{
    entityType: string;
    entityId: string;
    entityLabel: string;
  } | null>(null);

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Partners" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Handshake className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Partners</h1>
          </div>
          <div className="flex items-center gap-2">
            {permissions?.canExport && <ExportButton module="partners" />}
            {permissions?.canCreate && (
              <Button onClick={() => setPartnerDrawer({ partner: null })}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                New Partner
              </Button>
            )}
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load partners</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              {companies.map((company) => (
                <OwnershipCard
                  key={company.id}
                  company={company}
                  canEdit={!!permissions?.canEdit}
                  canDelete={!!permissions?.canDelete}
                  onEdit={() => setOwnershipCompany(company)}
                  onRemove={(effectiveFrom) => setRemoveTarget({ company, effectiveFrom })}
                  onActivity={() =>
                    setActivity({
                      entityType: "partner_ownership",
                      entityId: company.id,
                      entityLabel: `${company.name} ownership`,
                    })
                  }
                />
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border bg-white" style={BORDER}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground" style={BORDER}>
                    <th className="px-6 py-3 font-medium">#</th>
                    <th className="px-6 py-3 font-medium">Name</th>
                    <th className="px-6 py-3 font-medium">Short code</th>
                    <th className="px-6 py-3 font-medium">Linked user</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin" strokeWidth={1.5} />
                      </td>
                    </tr>
                  )}
                  {!isLoading && partners.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                        No partners yet
                      </td>
                    </tr>
                  )}
                  {partners.map((p) => (
                    <tr key={p.id} className="border-b last:border-0" style={BORDER}>
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{p.code}</td>
                      <td className="px-6 py-4 font-medium">{p.name}</td>
                      <td className="px-6 py-4 text-muted-foreground">{p.shortCode}</td>
                      <td className="px-6 py-4 text-muted-foreground">{p.user?.name ?? "—"}</td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {p.isActive ? "Active" : "Inactive"}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setActivity({
                              entityType: "partner",
                              entityId: p.id,
                              entityLabel: p.name,
                            })
                          }
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
                          title="Activity history"
                        >
                          <History className="h-[14px] w-[14px]" strokeWidth={1.5} />
                        </button>
                        {permissions?.canEdit && (
                          <button
                            type="button"
                            onClick={() => setPartnerDrawer({ partner: p })}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
                            title="Edit"
                          >
                            <Pencil className="h-[14px] w-[14px]" strokeWidth={1.5} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {partnerDrawer && (
        <PartnerDrawer partner={partnerDrawer.partner} onClose={() => setPartnerDrawer(null)} />
      )}
      {ownershipCompany && (
        <OwnershipDrawer
          company={ownershipCompany}
          partners={partners.filter((p) => p.isActive)}
          onClose={() => setOwnershipCompany(null)}
        />
      )}
      {activity && (
        <ActivityHistoryDrawer isOpen onClose={() => setActivity(null)} {...activity} />
      )}
      <ConfirmDialog
        open={!!removeTarget}
        title="Remove ownership record?"
        description={
          removeTarget
            ? `This removes the ${removeTarget.company.name} ownership effective ${formatDate(removeTarget.effectiveFrom)}.`
            : ""
        }
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        variant="destructive"
        confirmLabel="Remove"
        onConfirm={async () => {
          if (removeTarget) {
            await removeOwnership.mutateAsync({
              companyId: removeTarget.company.id,
              effectiveFrom: removeTarget.effectiveFrom,
            });
          }
          setRemoveTarget(null);
        }}
      />
    </AppLayout>
  );
}
